import { type ReactNode, useLayoutEffect, useState } from "react";

import {
  AgentsIcon,
  Box,
  Column,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
} from "@hightouch/htui";

import { LlmDisclaimer } from "src/components/llm/components/disclaimer";
import {
  HIDDEN_SCROLLBAR_SX,
  useChatGutter,
} from "src/components/llm/context/chat-gutter-context";
import {
  DEFAULT_SCROLL_FADE_HEIGHT_PX,
  useScrollFade,
} from "src/hooks/use-scroll-fade";

// Eyebrow hosts use the same gap when cancelling an exiting strip's flex slot.
export const EMBEDDED_AGENT_COMPOSER_GAP = 2;

// Reserve three suggestion cards so changing or hiding suggestions does not recenter the introduction.
const SUGGESTIONS_SLOT_MIN_HEIGHT_PX = 3 * (12 * 2 + 16 + 4 + 16) + 2 * 12;

type LayoutProps = {
  surface?: "panel" | "sidebar";
  hideEmptyState?: boolean;
  /** Centers the composer with the introduction, suggestions below it, instead of pinning it to the bottom. Applies only to the full-pane sidebar view. */
  centerComposer?: boolean;
  marginTop?: number | string;
  bottomOffset?: number | string;
};

type Props = LayoutProps & {
  introduction: ReactNode;
  suggestions: ReactNode;
  composer: ReactNode;
  composerEyebrow?: ReactNode;
};

/** Shared initial chat layout; hosts own prompt state, suggestions, and submission. */
export function EmbeddedAgentInitialView({
  surface = "sidebar",
  hideEmptyState = false,
  centerComposer = false,
  marginTop = 8,
  bottomOffset = 0,
  introduction,
  suggestions,
  composer,
  composerEyebrow,
}: Props) {
  const fillsPane = surface === "sidebar" && !hideEmptyState;
  const isCentered = fillsPane && centerComposer;
  const chatGutter = useChatGutter();
  // State rather than a ref, so measuring follows the slot when centering moves it below the composer.
  const [suggestionsElement, setSuggestionsElement] =
    useState<HTMLDivElement | null>(null);
  // Centered, an empty reserve would push the introduction and composer above the middle.
  const [suggestionsHeight, setSuggestionsHeight] = useState(
    centerComposer ? 0 : SUGGESTIONS_SLOT_MIN_HEIGHT_PX,
  );

  useLayoutEffect(() => {
    const element = suggestionsElement;
    if (!fillsPane || !element) return;

    // Retain room for wrapped cards and template controls when suggestions disappear.
    const measure = () =>
      setSuggestionsHeight((height) => Math.max(height, element.offsetHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [fillsPane, suggestionsElement]);

  const contentScrollFade = useScrollFade(
    DEFAULT_SCROLL_FADE_HEIGHT_PX,
    "both",
    {
      fadeOnlyAfterScroll: true,
    },
  );

  const suggestionsSlot = (
    <Box
      ref={setSuggestionsElement}
      width="100%"
      {...(fillsPane && {
        minHeight: `${suggestionsHeight}px`,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column" as const,
        // Cards stay against the composer, leaving any reserved space on the far side.
        justifyContent: isCentered
          ? ("flex-start" as const)
          : ("flex-end" as const),
      })}
      {...(isCentered
        ? {
            // With the introduction's top margin, centers the group and still scrolls it from the top when it overflows.
            mb: "auto",
            px: chatGutter?.conversation ?? 4,
            pb: 4,
          }
        : fillsPane && { mt: "auto" })}
    >
      {suggestions}
    </Box>
  );

  return (
    <Column
      align="center"
      width="100%"
      gap={fillsPane ? 0 : 4}
      {...(fillsPane
        ? {
            pt: marginTop,
            height: "100%",
            minHeight: 0,
            overflowY: isCentered ? "auto" : "hidden",
          }
        : {
            // History scrolls above this block; a top margin would separate it from the scroll fade.
            ...(surface === "sidebar" ? { flexShrink: 0 } : { mt: marginTop }),
            position: "sticky" as const,
            bottom: bottomOffset,
          })}
    >
      <Column
        ref={contentScrollFade.ref}
        gap={4}
        width="100%"
        px={chatGutter?.conversation ?? 4}
        justifyContent={fillsPane ? "flex-start" : "center"}
        flex={isCentered ? "none" : "1"}
        sx={{
          ...contentScrollFade.sx,
          ...(chatGutter !== null && HIDDEN_SCROLLBAR_SX),
        }}
        {...(isCentered
          ? { mt: "auto", pb: 6 }
          : fillsPane && {
              minHeight: 0,
              overflowY: "auto",
              // Keep card shadows and their entrance motion inside the scroll clip.
              pb: 4,
            })}
      >
        {surface === "panel"
          ? introduction
          : !hideEmptyState && (
              <Column
                align="center"
                justify="center"
                flexGrow={isCentered ? 0 : 1}
                flexShrink={0}
                gap={4}
                pointerEvents="none"
              >
                <Box
                  as={AgentsIcon}
                  boxSize={16}
                  color="base.border"
                  flexShrink={0}
                />
                {introduction}
              </Column>
            )}
        {!isCentered && suggestionsSlot}
      </Column>
      <Column
        backgroundColor={
          surface === "sidebar" ? "transparent" : "base.backgroundHover"
        }
        flexShrink={0}
        px={chatGutter?.composer ?? 4}
        pt={surface === "sidebar" ? 0 : 4}
        gap={EMBEDDED_AGENT_COMPOSER_GAP}
        borderTopRadius="3xl"
        width="100%"
        // Eyebrow banners can tuck behind the composer without escaping this surface.
        isolation="isolate"
      >
        {composerEyebrow}
        <Row gap={4} width="100%" maxWidth="3xl" align="center">
          {composer}
        </Row>
        <LlmDisclaimer mb={4} />
      </Column>
      {/* After the composer rather than in the column above, so the composer keeps its place in the tree and stays mounted. */}
      {isCentered && suggestionsSlot}
    </Column>
  );
}

/** Default `introduction` for hosts that show a title and a one-line description. */
export function EmbeddedAgentIntroduction({
  title,
  description,
}: {
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <Column align="center" gap={1} px={4} textAlign="center">
      <Text size="lg" fontWeight="medium" color="text.primary">
        {title}
      </Text>
      <Text size="sm" color="text.secondary">
        {description}
      </Text>
    </Column>
  );
}

/** Uses the loaded layout so the composer holds its place while suggestions load. */
export function EmbeddedAgentInitialViewSkeleton(props: LayoutProps) {
  return (
    <EmbeddedAgentInitialView
      {...props}
      introduction={
        <Skeleton isLoading>
          <SkeletonBox height={5} width="2xs" borderRadius="full" />
        </Skeleton>
      }
      suggestions={
        // Centered, placeholder cards would lift the composer above where a view without suggestions puts it.
        !props.centerComposer && (
          <Skeleton isLoading>
            <Column gap={3} width="100%">
              {[0, 1, 2].map((index) => (
                <SkeletonBox
                  key={index}
                  height={16}
                  width="100%"
                  borderRadius="2xl"
                />
              ))}
            </Column>
          </Skeleton>
        )
      }
      composer={
        <Box width="100%">
          <Skeleton isLoading>
            <SkeletonBox height={24} width="100%" borderRadius="2xl" />
          </Skeleton>
        </Box>
      }
    />
  );
}
